// https://github.com/sindresorhus/is-network-error
const objectToString = Object.prototype.toString;

const isError = (value: unknown): value is Error =>
  objectToString.call(value) === '[object Error]';

const errorMessages = new Set([
  'network error',
  'NetworkError when attempting to fetch resource.',
  'The Internet connection appears to be offline.',
  'Network request failed',
  'fetch failed',
  'terminated',
  ' A network error occurred.',
  'Network connection lost',
]);

export const isNetworkError = (error: unknown): boolean => {
  const isValid =
    error != null &&
    isError(error) &&
    error.name === 'TypeError' &&
    typeof error.message === 'string';

  if (!isValid) {
    return false;
  }

  const { message, stack } = error;

  if (
    message === 'Load failed' ||
    (message.startsWith('Load failed (') && message.endsWith(')'))
  ) {
    return stack === undefined || '__sentry_captured__' in error;
  }

  if (message.startsWith('error sending request for url')) {
    return true;
  }

  if (
    message === 'Failed to fetch' ||
    (message.startsWith('Failed to fetch (') && message.endsWith(')'))
  ) {
    return true;
  }

  return errorMessages.has(message);
};

const isAbortError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

const wrappedTransportError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  if ('networkError' in error && error.networkError != null) {
    return error.networkError;
  }

  if (error instanceof Error) {
    return error.cause;
  }

  return undefined;
};

export const isClientNetworkFailure = (error: unknown) => {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current != null && !seen.has(current)) {
    seen.add(current);
    if (isAbortError(current) || isNetworkError(current)) {
      return true;
    }
    current = wrappedTransportError(current);
  }

  return false;
};
